import React, { useEffect, useRef, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface GoogleSignInButtonProps {
  onSuccess: (credential: string) => void;
  onError?: () => void;
  text?: 'signin_with' | 'signup_with';
  disabled?: boolean;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: {
            client_id: string;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
            callback: (response: { credential: string }) => void;
          }) => void;
          prompt: (momentNotification?: (notification: any) => void) => void;
          renderButton: (element: HTMLElement, config: any) => void;
        };
      };
    };
  }
}

const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  onSuccess,
  onError,
  text = 'signin_with',
  disabled = false,
}) => {
  const { theme } = useTheme();
  const buttonRef = useRef<HTMLDivElement>(null);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
  
  // Use refs to store callbacks so they don't cause re-initialization
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  
  // Update refs when callbacks change
  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  }, [onSuccess, onError]);

  useEffect(() => {
    // Load Google Identity Services script
    if (!window.google) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);

      script.onload = () => {
        setIsScriptLoaded(true);
      };
    } else {
      setIsScriptLoaded(true);
    }
  }, []);

  useEffect(() => {
    // Initialize and render Google Identity Services button when script is loaded
    if (!isScriptLoaded || !window.google || !googleClientId || !buttonRef.current) {
      return;
    }

    // Clear any existing button
    buttonRef.current.innerHTML = '';

    // Get the container width in pixels to pass to Google's renderButton
    const getContainerWidth = () => {
      if (buttonRef.current) {
        return buttonRef.current.offsetWidth || buttonRef.current.clientWidth || 400;
      }
      return 400;
    };

    const containerWidth = getContainerWidth();

    // Initialize Google Identity Services
    // Enable one-tap UI with auto_select and prompt()
    window.google.accounts.id.initialize({
      client_id: googleClientId,
      auto_select: true, // Enable automatic account selection (one-tap UI)
      cancel_on_tap_outside: true, // Cancel if user taps outside
      callback: (response) => {
        if (response.credential) {
          onSuccessRef.current(response.credential);
        } else {
          if (onErrorRef.current) {
            onErrorRef.current();
          }
        }
      },
    });

    // Render the Google button with explicit width parameter
    // Use 'outline' theme for both light and dark modes
    // Use 'center' logo alignment so logo and text are grouped together
    window.google.accounts.id.renderButton(buttonRef.current, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: text,
      shape: 'rectangular',
      logo_alignment: 'center',
      width: containerWidth, // Set explicit width in pixels
      disabled: disabled,
    });

    // Show one-tap UI prompt after a short delay to ensure everything is initialized
    setTimeout(() => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.prompt((notification) => {
          // Handle prompt notifications if needed
          // notification.isNotDisplayed() - one-tap UI not displayed
          // notification.isSkippedMoment() - user dismissed the prompt
          // notification.isDismissedMoment() - user dismissed the prompt
        });
      }
    }, 100);

    // Watch for container size changes and update button width if needed
    const resizeObserver = new ResizeObserver(() => {
      if (buttonRef.current && window.google) {
        const newWidth = getContainerWidth();
        const googleButton = buttonRef.current.querySelector('div[role="button"]') as HTMLElement;
        if (googleButton && googleButton.offsetWidth !== newWidth) {
          // Re-render with new width if container size changed
          buttonRef.current.innerHTML = '';
          window.google.accounts.id.renderButton(buttonRef.current, {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            text: text,
            shape: 'rectangular',
            logo_alignment: 'center',
            width: newWidth,
            disabled: disabled,
          });
          // Re-show one-tap UI after re-render
          setTimeout(() => {
            if (window.google?.accounts?.id) {
              window.google.accounts.id.prompt();
            }
          }, 100);
        }
      }
    });

    if (buttonRef.current) {
      resizeObserver.observe(buttonRef.current);
    }

    // Cleanup
    return () => {
      resizeObserver.disconnect();
    };
  }, [isScriptLoaded, googleClientId, text, theme, disabled]);

  return (
    <div
      ref={buttonRef}
      className={`w-full ${disabled ? 'opacity-50 pointer-events-none' : ''}`}
      aria-label={text === 'signin_with' ? 'Sign in with Google' : 'Sign up with Google'}
    />
  );
};

export default GoogleSignInButton;

